<template lang="pug">
  .wrap(v-loading='showOverallLoader')
    template(v-if='showCharts')
      page-title Your Sales Overview

      el-button-group(:style="{marginBottom: '25px'}")
        a(:href="reportSalesByProductCsvUrl")
          el-button Download Sales by Product
        a(:href="reportSalesByUserCsvUrl" :style="{marginLeft: '10px'}")
          el-button Download Sales by User
        a(:href="breakdownReportExcelUrl" :style="{marginLeft: '10px'}")
          el-button Download Product Breakdown

      .location-switcher
        .location-switcher-label Location:
        el-select.location-dropdown(size='large' placeholder='All' v-model='selectedLocationId' @visible-change='onLocationSelectDropdown' :loading='locations.loading')
          el-option(value='' label='All' key='all') All
          el-option(v-for='location in locations.list' :value='location.id' :label='location.name' :key='location.id') {{ location.name }}

      time-switcher(v-model='apiDateRange')

      div(v-loading='showChartsLoader')
        .charts
          .circles
            .circle
              circular-counter(:key='stats.overall.data.totals.quantity' id='quantity' label='sales' :total='stats.overall.data.totals.totalQuantity'
                :count='stats.overall.data.totals.quantity' color='#f45943' :size='150')

            .circle
              circular-counter(:key='stats.overall.data.totals.volume' id='volume' :count-label='formatCompanyCurrency(stats.overall.data.totals.volume, "0.a")'
                label='value' :total='stats.overall.data.totals.totalVolume' :count='stats.overall.data.totals.volume' color='#71c8bf' :size='150')

          el-row(:gutter='20')
            el-col(:span='12')
              chart-wrap
                bar-chart(:chart-data="dailyQuantityBreakdownChartData" :options='dailyQuantityBreakdownChartOptions' :height='300')

            el-col(:span='12')
              chart-wrap
                bar-chart(:chart-data="dailyVolumeBreakdownChartData" :options='dailyVolumeBreakdownChartOptions' :height='300')

        .charts
          .line-chart-wrap
            .legends
              .legend(v-for="(item, index) in overallReportLegend" :key="index")
                span(:style="{ borderColor: `${item.colour}` }")
                p {{item.label}}
            chart-wrap
              line-chart(:chart-data="categoryQuantityByDayChartData" :options='categoryQuantityByDayChartOptions' :height='300')

        .tabs
          .tab(v-for='(category, categoryIndex) in categories.list' :key='category.id' @click='selectCategory(category.id)'
            :class='{ active: selectedCategoryId === category.id }')
            | {{ category.name }}
          .tab(key='no-category' @click='selectCategory("no-category")' :class='{ active: selectedCategoryId === "no-category" }')
            | No category

        .charts
          el-row(:gutter='20')
            el-col(:span='12')
              .category-counter
                circular-counter(:key='selectedCategoryStats.data.totals.categoryQuantity' id='category-quantity'
                  label='sales' :total='selectedCategoryStats.data.totals.totalQuantity'
                  :count='selectedCategoryStats.data.totals.categoryQuantity' color='#f45943')

              chart-wrap
                bar-chart(:chart-data="categoryProductBreakdownChartData" :options='categoryProductBreakdownChartOptions' :height='300')

            el-col(:span='12')
              h3(:style='{ marginBottom: "10px" }') Top Sellers

              el-table(:data="selectedCategoryStats.data.topLeaderboard" empty-text="No entries" v-loading='selectedCategoryStats.loading')
                el-table-column(width="100" align='center' label='Rank')
                  template(slot-scope="scope") # {{ `${scope.$index + 1}` }}

                el-table-column(label='Name')
                  template(slot-scope="scope") {{ scope.row.firstName }} {{ scope.row.lastName }}

                el-table-column(label='Quantity' align='center')
                  template(slot-scope="scope") {{ scope.row.quantity }}

                el-table-column(label='Value' align='center')
                  template(slot-scope="scope") {{ formatCompanyCurrency(scope.row.volume) }}

    .empty(v-if='showPlaceholder')
      sales-empty-state(:cta-route="{ name: 'brapp:sales:productCreate' }")

</template>

<script>
import _ from "lodash";
import format from "date-fns/format";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import eachDay from "date-fns/each_day";
import truncateMiddle from "truncate-middle";

import FormattingMixin from "@/shared/mixins/Formatting";
import ChartWrap from "@/shared/components/ChartWrap";

import { mapGetters, mapActions } from "vuex";

import PageTitle from "@/shared/components/layout/PageTitle";
import CircularCounter from "@/shared/components/CircularCounter";
import TimeSwitcher from "@/brapp/components/shared/TimeSwitcher";
import salesEmptyState from "../../salesEmptyState";
import LineChart from "./LineChart";
import BarChart from "./BarChart";

const formatApiDate = date => format(date, "YYYY-MM-DD");

const DEFAULT_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  layout: {
    padding: 20
  },
  scales: {
    yAxes: [
      {
        gridLines: {
          display: true,
          color: "rgba(0, 0, 0, 0.03)"
        }
      }
    ]
  }
};

export default {
  mixins: [FormattingMixin],
  components: {
    TimeSwitcher,
    LineChart,
    CircularCounter,
    BarChart,
    PageTitle,
    salesEmptyState,
    ChartWrap
  },
  data() {
    const now = Date.now();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    const apiDateRange = [formatApiDate(start), formatApiDate(end)];

    return {
      initialStatsLoaded: false,
      stats: {
        overall: {
          loading: true,
          data: {
            dailyVolumeBreakdown: [],
            dailyQuantityBreakdown: [],
            categoryQuantityBreakdown: [],
            totals: {
              quantity: 0,
              volume: 0,
              totalQuantity: 0,
              totalVolume: 0
            }
          }
        }
      },
      categories: {
        loading: true,
        list: []
      },
      locations: {
        loading: true,
        list: []
      },
      selectedLocationId: null,
      selectedCategoryId: null,
      selectedCategoryStats: {
        loading: true,
        data: {
          totals: {
            categoryQuantity: 0,
            totalQuantity: 0
          },
          productQuantityBreakdown: [],
          topLeaderboard: []
        }
      },
      apiDateRange,
      dateRange: eachDay(start, end),
      dailyVolumeBreakdownChartOptions: _.merge({}, DEFAULT_CHART_OPTIONS, {
        scales: {
          yAxes: [
            {
              ticks: {
                callback: value => this.formatCompanyCurrency(value)
              }
            }
          ]
        }
      }),
      dailyQuantityBreakdownChartOptions: DEFAULT_CHART_OPTIONS,
      categoryQuantityByDayChartOptions: DEFAULT_CHART_OPTIONS,
      categoryProductBreakdownChartOptions: _.merge({}, DEFAULT_CHART_OPTIONS, {
        scales: {
          xAxes: [
            {
              ticks: {
                autoSkip: false,
                maxRotation: 60,
                minRotation: 30,
                callback: value => truncateMiddle(value, 8, 8, "...")
              }
            }
          ]
        }
      })
    };
  },
  computed: {
    ...mapGetters("api", ["generateUrl"]),
    ...mapGetters("auth", ["activeCompany"]),
    showPlaceholder() {
      return (
        this.initialStatsLoaded &&
        !this.stats.overall.loading &&
        !this.stats.overall.data.totals.totalQuantity
      );
    },
    showCharts() {
      return (
        this.initialStatsLoaded && this.stats.overall.data.totals.totalQuantity
      );
    },
    showOverallLoader() {
      return !this.initialStatsLoaded && this.stats.overall.loading;
    },
    showChartsLoader() {
      return this.initialStatsLoaded && this.stats.overall.loading;
    },
    reportParams() {
      return {
        start: formatApiDate(this.apiDateRange[0]),
        end: formatApiDate(this.apiDateRange[1]),
        company: _.get(this.activeCompany, "id"),
        location: this.selectedLocationId
      };
    },
    reportSalesByProductCsvUrl() {
      return this.generateUrl({
        path: "/brapp/sales/reports/overall/",
        params: { ...this.reportParams, format: "csv" }
      });
    },
    reportSalesByUserCsvUrl() {
      return this.generateUrl({
        path: "/brapp/sales/reports/leaderboard/",
        params: { ...this.reportParams, format: "csv" }
      });
    },
    breakdownReportExcelUrl() {
      return this.generateUrl({
        path: "/brapp/sales/reports/breakdown/excel/",
        params: this.reportParams
      });
    },
    chartColours() {
      return ["#f45943", "#74c6c0", "#5d79b5", "#f8a523"];
    },
    overallReportLegend() {
      return this.stats.overall.data.categoryQuantityBreakdown.map(
        (group, groupIndex) => ({
          colour: this.chartColours[groupIndex],
          label: group.name
        })
      );
    },
    categoryQuantityByDayChartData() {
      const currentRange = eachDay(this.apiDateRange[0], this.apiDateRange[1]);

      const labels = currentRange.map(d => format(d, "D MMM"));
      const datasets = this.stats.overall.data.categoryQuantityBreakdown.map(
        (group, groupIndex) => ({
          fill: false,
          pointRadius: 4,
          lineTension: 0,
          borderColor: this.chartColours[groupIndex],
          borderWidth: 2,
          label: group.name,
          data: currentRange.map(date => {
            const formattedDate = format(date, "YYYY-MM-DD");
            const dateEntry = group.days.find(d => d.date === formattedDate);
            return dateEntry ? dateEntry.quantity : 0;
          })
        })
      );
      return { labels, datasets };
    },
    dailyQuantityBreakdownChartData() {
      const currentRange = eachDay(this.apiDateRange[0], this.apiDateRange[1]);
      const labels = currentRange.map(d => format(d, "dd")[0]);
      const datasets = [
        {
          backgroundColor: "#f45943",
          label: "",
          data: this.stats.overall.data.dailyQuantityBreakdown.map(
            day => day.quantity
          )
        }
      ];
      return { labels, datasets };
    },
    dailyVolumeBreakdownChartData() {
      const currentRange = eachDay(this.apiDateRange[0], this.apiDateRange[1]);
      const labels = currentRange.map(d => format(d, "dd")[0]);
      const datasets = [
        {
          backgroundColor: "#71c8bf",
          label: "",
          data: this.stats.overall.data.dailyVolumeBreakdown.map(
            day => day.volume
          )
        }
      ];
      return { labels, datasets };
    },
    categoryProductBreakdownChartData() {
      const productBreakdown = this.selectedCategoryStats.data
        .productQuantityBreakdown;
      const labels = productBreakdown.map(p => p.name);
      const datasets = [
        {
          backgroundColor: "#f45943",
          label: "",
          data: productBreakdown.map(product => product.quantity)
        }
      ];
      return { labels, datasets };
    }
  },
  watch: {
    apiDateRange(newVal) {
      this.onRangeChange(newVal);
    },
    selectedLocationId() {
      this.loadAllStats();
    }
  },
  methods: {
    ...mapActions("products", ["fetchCategories", "fetchLocations"]),
    ...mapActions("reports", [
      "fetchSalesOverallStats",
      "fetchSalesCategoryStats"
    ]),
    loadOverallStats({ start, end }) {
      this.stats.overall.loading = true;
      return this.fetchSalesOverallStats({
        params: { start, end, location: this.selectedLocationId }
      }).then(data => {
        this.stats.overall.loading = false;
        this.stats.overall.data = data;
        return data;
      });
    },
    loadCategoryStats(params = {}) {
      return this.fetchSalesCategoryStats({
        params: { location: this.selectedLocationId, ...params }
      }).then(data => {
        this.selectedCategoryStats.loading = false;
        this.selectedCategoryStats.data = data;
        return data;
      });
    },
    loadCategories() {
      this.categories.loading = true;
      return this.fetchCategories({ params: { sort: "name" } }).then(data => {
        this.categories.list = data;
        this.categories.loading = false;
        return data;
      });
    },
    loadLocations() {
      this.locations.loading = true;
      return this.fetchLocations({ params: { sort: "name" } }).then(data => {
        this.locations.list = data;
        this.locations.loading = false;
        return data;
      });
    },
    loadAllStats() {
      const params = { start: this.apiDateRange[0], end: this.apiDateRange[1] };
      return Promise.all([
        this.loadOverallStats(params),
        this.loadCategories().then(categories => {
          this.selectedCategoryId = categories.length
            ? categories[0].id
            : "no-category";
          return this.loadCategoryStats({
            ...params,
            category: this.selectedCategoryId
          });
        })
      ]);
    },
    onRangeChange(dateRange) {
      const params = {
        start: formatApiDate(dateRange[0]),
        end: formatApiDate(dateRange[1]),
        location: this.selectedLocationId
      };
      this.loadOverallStats(params);
      this.loadCategoryStats({ ...params, category: this.selectedCategoryId });
    },
    selectCategory(categoryId) {
      const params = {
        start: this.apiDateRange[0],
        end: this.apiDateRange[1],
        location: this.selectedLocationId
      };
      this.selectedCategoryId = categoryId;
      this.loadCategoryStats({ ...params, category: categoryId });
    },
    onLocationSelectDropdown(open) {
      if (open) {
        this.loadLocations();
      }
    }
  },
  created() {
    this.loadAllStats().then(() => {
      this.initialStatsLoaded = true;
    });
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../../assets/scss/mixins.scss";
.charts {
  background: white;
  padding: 20px;
  margin-bottom: 20px;
}

.summary-chart-wrap {
  margin: 20px 0;
}

.line-chart-wrap {
  margin-top: 30px;
  padding: 0 30px;
}

.detail {
  margin-top: 30px;
  background: white;
  padding: 30px;
}

.detail-count {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.category-counter {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.accordion-row {
  display: flex;
  align-items: center;
  flex-direction: row;
}

.accordion-row-label {
  flex: 0 0 auto;
}

.accordion-row-count {
  flex: 0 0 auto;
  margin-left: auto;
}

.circles {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.circle {
  margin: 0 40px;
}

.tabs {
  display: flex;
  align-items: center;
  flex-direction: row;
  margin-top: 30px;
  margin-bottom: 20px;
  justify-content: center;
}

.tab {
  background: white;
  margin: 0 4px;
  padding: 10px 20px;
  align-items: center;
  flex-direction: column;
  border: 1px solid #eee;
  font-size: 20px;
  cursor: pointer;
  transition: background 250ms ease-in-out;

  &.active {
    background: #2c3e50;
    color: white;
  }
}

.location-switcher {
  margin-bottom: 20px;
}

.location-switcher-label {
  font-size: 24px;
  margin-bottom: 15px;
}

.empty-state {
  min-height: 300px;
  font-size: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}
.emptystate {
  width: 100%;
}
</style>
